import type { Plugin } from 'vue'
import { useSessionStorage } from '@vueuse/core'
import { LocalStorageKeys, LocalStorageKeyManager } from '~/store/LocalStorageKeyManager'

type LastReload = {
  href: string
  at: number
}

const RELOAD_WINDOW_MS = 10_000

const lastReload = useSessionStorage<LastReload>(
  LocalStorageKeyManager.createStorageKey(LocalStorageKeys.CHUNK_LOAD_RELOAD),
  { href: '', at: 0 },
  { flush: 'sync' },
)

const hasReloadedRecently = (): boolean => {
  return (
    lastReload.value.href === window.location.href &&
    Date.now() - lastReload.value.at < RELOAD_WINDOW_MS
  )
}

const rememberReload = (): void => {
  lastReload.value = { href: window.location.href, at: Date.now() }
}

/**
 * Reloads the app when a lazy chunk fails to load, typically because a deploy replaced the
 * hashed assets under a running tab.
 *
 * Firefox and Safari abort in-flight dynamic imports as soon as a navigation starts and reject
 * them, which Vite reports as a preload error. Reloading at that point cancels the navigation
 * (e.g. the redirect to Previo ID) and loops. Firefox fires `beforeunload` first, so the reload
 * is skipped once the page is leaving; iOS Safari fires no event before the rejection, so the
 * reload is additionally bounded to once per URL within a short window.
 */
export const chunkLoadRecovery: Plugin = {
  install: (): void => {
    let isLeavingPage = false

    window.addEventListener('beforeunload', () => {
      isLeavingPage = true
    })

    window.addEventListener('vite:preloadError', () => {
      if (isLeavingPage || hasReloadedRecently()) {
        return
      }

      rememberReload()
      window.location.reload()
    })
  },
}
